import { ChangeDetectorRef, Component, Input, OnInit, effect, inject, signal, untracked } from '@angular/core';
import { TitleCasePipe } from '@angular/common';
import { Store } from '@ngrx/store';
import { EdgeAssessmentsService } from '@app/services/api/edge-assessments.service';
import { ChecklistService } from '@app/services/api/checklist/checklist.service';
import { ConfigService } from '@app/services/config-service';
import { AuthService } from '@app/services/auth/auth.service';
import { MatDialog } from '@angular/material/dialog';
import { 
  BYP_PLAN_GENERATED_LOCAL_KEY,
  CAREER_GOAL_LOCAL_KEY, 
  CAREER_RESULT_CHECKLIST_ITEMS, 
  ItemTags, 
  ListItem, 
  USER_RESUME_LOCAL_KEY
} from '@app/utils/constants';
import { catchError, finalize, of, switchMap, tap } from 'rxjs';
import { CareerGoalDialogComponent } from '../career-goal-dialog/career-goal-dialog.component';
import { AddResumeDialogComponent, AddResumeDialogResult } from '../add-resume-dialog/add-resume-dialog.component';
import {
  StartAssessmentDialogComponent
} from '@app/shared/edge/start-assessment-dialog/start-assessment-dialog.component';
import { MatTooltipModule } from '@angular/material/tooltip';
import { UserBiqData } from '@app/schemas/biq';
import { CareerChecklistStore } from '@app/services/signals/career-checklist-service';
import { Router } from '@angular/router';
import { stringToBoolean } from '@app/utils/sharedFunctions';
import { analytics } from '@app/events/segment/analytics';
import { JobLandingDialogComponent, JobLandingDialogResult } from '@app/shared/dialogs/job-landing-dialog/job-landing-dialog.component';
import { ReportedWorkExperienceService } from '@app/services/api/reported-work-experience/reported-work-experience.service';

@Component({
  selector: 'app-left-bar',
  imports: [
    MatTooltipModule,
    TitleCasePipe,
  ],
  templateUrl: './left-bar.component.html',
  styleUrl: './left-bar.component.css'
})
export class LeftBarComponent implements OnInit {
  readonly itemDialog = inject(MatDialog);
  readonly assessmentDialogPanelClass = 'edge-assessment-dialog';
  readonly assessmentDialogWidth = '650px';
  readonly careerGoalDialogWidth = '650px';

  @Input() section: 'explore' | 'plan' = 'explore';

  @Input()
  set edgeAssessmentId(assessmentId: string) {
    if(!assessmentId) return;

    if (assessmentId === this.configService.edgeCommunicationAssessmentId) {
      this.markAsCompleted('communication');
    } 

    if (assessmentId === this.configService.edgeBehavioralAssessmentId) {
      this.markAsCompleted('durable');
    }

    this.getNextToDoItem();
  }

  edgeCommunicationAssessmentUrl: string;
  _edgeCommunicationStatus: string;
  edgeBehavioralAssessmentUrl: string;
  _edgeBehavioralStatus: string;
  userCareerGoal!: string;
  userProfile!: UserBiqData;
  checklistItems = signal<ListItem[]>(CAREER_RESULT_CHECKLIST_ITEMS.map(item => ({ ...item })));
  lastCheckListItemIndex = CAREER_RESULT_CHECKLIST_ITEMS.length - 1;
  private initialLoadComplete = false;

  constructor(
    private store: Store<{
      resumeDetails: boolean,
      careerGoal: string,
      userBiq: UserBiqData,
    }>,
    private configService: ConfigService,
    private authService: AuthService,
    private edgeAssessmentsService: EdgeAssessmentsService,
    private checklistService: ChecklistService,
    private checklistStore: CareerChecklistStore,
    private router: Router,
    private cdr: ChangeDetectorRef,
    private reportedWorkExperienceService: ReportedWorkExperienceService,
  ) {
    this.store.select('userBiq').subscribe(value => this.userProfile = value);

    this._edgeCommunicationStatus = 'pending';
    this._edgeBehavioralStatus = 'pending';

    const tenantId = this.authService.getCurrentUser()?.tenantId;
    this.edgeCommunicationAssessmentUrl = this.configService.getCommunicationAssessmentUrl(tenantId);
    this.edgeBehavioralAssessmentUrl = this.configService.getBehavioralAssessmentUrl(tenantId);

    effect(() => {
      const planGenerated = this.checklistStore.buildPlanGenerated();
      if (planGenerated) {
        untracked(() => {
          this.markAsCompleted('plan');
          this.getNextToDoItem();
        });
      }
    });
  }

  get edgeCommunicationStatus() {
    return this._edgeCommunicationStatus;
  }

  set edgeCommunicationStatus(value: string) {
    this._edgeCommunicationStatus = value;
    this.onEdgeStatusChange(value, 'communication');
  }

  get edgeBehavioralStatus() {
    return this._edgeBehavioralStatus;
  }

  set edgeBehavioralStatus(value: string) {
    this._edgeBehavioralStatus = value;
    this.onEdgeStatusChange(value, 'durable');
  }

  get sortedChecklist(): ListItem[] {
    const nonJob = this.checklistItems().filter(i => i.tag !== 'job');
    const jobItem = this.checklistItems().find(i => i.tag === 'job');
    const completed = nonJob.filter(i => i.status === 'completed');
    const incomplete = nonJob.filter(i => i.status !== 'completed');

    if (jobItem?.status === 'completed') {
      return [...completed, jobItem, ...incomplete];
    }
    return [...completed, ...incomplete, ...(jobItem ? [jobItem] : [])];
  }

  ngOnInit() {
    this.store.select('careerGoal').subscribe(value => {
      this.userCareerGoal = value;
      this.cdr.detectChanges();
    });

    // Fetch checklist status from API (includes resume, goal, and plan status)
    this.fetchChecklistStatus();
    this.callEdgeAssessmentApi();

    //TODO: Remove - temporary override for local testing
    // setTimeout(() => {
    //   const list = this.checklistItems();
    //   const resumeItem = list.find(e => e.tag === 'resume');
    //   if (resumeItem) {
    //     resumeItem.status = 'todo';
    //     resumeItem.statusText = 'TO DO';
    //     this.checklistItems.set([...list]);
    //   }
    // }, 3000);
  }

  fetchChecklistStatus() {
    this.checklistService.getChecklistStatus().pipe(
      tap(response => {
        console.log('Checklist status:', response);
        
        // Update resume status
        if (response.resume_upload) {
          this.markAsCompleted('resume');
          // Update localStorage for backward compatibility
          localStorage.setItem(USER_RESUME_LOCAL_KEY, 'true');
        }
        // Update job shared status
        if (response.job_shared) {
          this.markAsCompleted('job');
        } else {
          this.markAsIncomplete('job');
        }
        // Update career goal status
        if (response.career_goal) {
          this.userCareerGoal = response.career_goal;
          this.markAsCompleted('goal');
          // Update localStorage for backward compatibility
          localStorage.setItem(CAREER_GOAL_LOCAL_KEY, response.career_goal);
          
          // Update plan status
          if (response.plan_generated) {
            this.markAsCompleted('plan');
            this.checklistStore.setBuildPlanGenerate();
            // Update localStorage for backward compatibility
            localStorage.setItem(BYP_PLAN_GENERATED_LOCAL_KEY, 'true');
          }
        } else {
          // No career goal set, lock the plan
          this.markAsIncomplete('goal');
          this.markAsLocked('plan');
        }
      }),
      catchError(error => {
        console.error('Error fetching checklist status:', error);
        
        // Fallback to localStorage/store if API fails
        this.store.select('resumeDetails').subscribe(value => {
          let userResumeUploaded = value;
          if (!value) {
            userResumeUploaded = stringToBoolean(localStorage.getItem(USER_RESUME_LOCAL_KEY));
          }

          if (userResumeUploaded) {
            this.markAsCompleted('resume');
          }
        });
        
        let userGoal = this.userCareerGoal;
        if (!userGoal) {
          userGoal = localStorage.getItem(CAREER_GOAL_LOCAL_KEY) || "";
        }
        
        if (userGoal) {
          this.markAsCompleted('goal');
          const isPlanGenerated = this.checklistStore.buildPlanGenerated() || 
                                  stringToBoolean(localStorage.getItem(BYP_PLAN_GENERATED_LOCAL_KEY));
          if (isPlanGenerated) {
            this.markAsCompleted('plan');
          }
        } else {
          this.markAsIncomplete('goal');
          this.markAsLocked('plan');
        }
        
        return of(null);
      }),
      finalize(() => {
        this.getNextToDoItem();
        this.initialLoadComplete = true;
      })
    ).subscribe();
  }

  callEdgeAssessmentApi() {
    const edgeCommunicationAssessmentId = this.configService.edgeCommunicationAssessmentId;
    const edgeBehavioralAssessmentId = this.configService.edgeBehavioralAssessmentId;

    this.edgeAssessmentsService.getAssessmentStatus(
      edgeCommunicationAssessmentId
    ).pipe(
      tap(response => {
        const { assessmentStatus } = response;
        this.edgeCommunicationStatus = assessmentStatus;
        console.log('Communication assessment status:', response);
      }),
      catchError(error => {
        if (error.status === 404) {
          this.edgeCommunicationStatus = 'not_started';
        }
        console.error('Error fetching communication assessment status:', error);
        return of(null);
      }),  
      switchMap(() => {
        return this.edgeAssessmentsService.getAssessmentStatus(
          edgeBehavioralAssessmentId
        );
      }),
      finalize(() => {
        this.getNextToDoItem();
      })
    ).subscribe({
      next: (response) => {
        const { assessmentStatus } = response;
        this.edgeBehavioralStatus = assessmentStatus;
        console.log('Behavioral assessment status:', response);
      },
      error: (error) => {
        if (error.status === 404) {
          this.edgeBehavioralStatus = 'not_started';
        }
        console.error('Error fetching behavioral assessment status:', error);
      }
    });
  }

  onClickStartAssessment(tag: ItemTags) {
    const tenantId = this.authService.getCurrentUser()?.tenantId;
    const assessmentUrl = tag == 'communication'
                          ? this.configService.getCommunicationAssessmentUrl(tenantId)
                          : this.configService.getBehavioralAssessmentUrl(tenantId);
    this.itemDialog.open(StartAssessmentDialogComponent, {
      panelClass: this.assessmentDialogPanelClass,
      maxWidth: this.assessmentDialogWidth,
      minWidth: this.assessmentDialogWidth,
      width: this.assessmentDialogWidth,
      data: {
        assessmentUrl: assessmentUrl,
        assessmentType: tag,
      }
    });

    return false;
  }

  openAddResumeDialog() {
    const resumeItem = this.checklistItems().find(e => e.tag === 'resume');
    const mode = resumeItem?.status === 'completed' ? 'view' : 'new';
    this.itemDialog.open(AddResumeDialogComponent, {
      panelClass: 'add-resume-dialog',
      width: '95%',
      maxWidth: '1632px',
      maxHeight: '95vh',
      data: { mode, section: this.section },
    }).afterClosed().subscribe((result?: AddResumeDialogResult) => {
      if (result?.resumeUploaded) {
        this.fetchChecklistStatus();
      }
    });
  }

  onEdgeStatusChange(status: string, tag: ItemTags) {
    if (status?.toLowerCase() == 'completed') {
      this.markAsCompleted(tag);
    }
  }

  markAsCompleted(tag: ItemTags) {
    const currentList = this.checklistItems();
    const itemIndex = currentList.findIndex(e => e.tag == tag);
    const previousStatus = currentList[itemIndex].status;
    currentList[itemIndex].status = "completed";
    this.checklistItems.set(currentList);

    if (this.initialLoadComplete && previousStatus !== 'completed') {
      analytics.track('Checklist Status Update', {
        section: this.section,
        item: tag,
        status: 'completed',
      });
    }
  }

  markAsIncomplete(tag: ItemTags) {
    const currentList = this.checklistItems();
    const itemIndex = currentList.findIndex(e => e.tag == tag);
    const itemStatus = currentList[itemIndex].status;
    if (itemStatus == "completed") {
      currentList[itemIndex].status = "pending";
      this.checklistItems.set(currentList);
    }
  }

  markAsLocked(tag: ItemTags) {
    const currentList = this.checklistItems();
    const itemIndex = currentList.findIndex(e => e.tag == tag);
    currentList[itemIndex].status = "locked";
    this.checklistStore.resetGeneratePlanUnlock();
    this.checklistItems.set(currentList);
  }

  getNextToDoItem() {
    const updatedChecklist: ListItem[] = this.sortedChecklist.map(item => {
      const status: ListItem['status'] = item.status === 'todo' ? 'pending' : item.status;

      return {
        ...item,
        status,
      };
    });
    const index = updatedChecklist.findIndex(
      item => item.status === 'pending' || item.status === 'locked'
    );

    if (index !== -1) {
      if (updatedChecklist[index].status === 'locked') {
        this.checklistStore.setGeneratePlanUnlock();
      }
      updatedChecklist[index].status = 'todo';
    }
    this.checklistItems.set(updatedChecklist);
  }

  onClickSetGoal() {
    this.itemDialog.open(CareerGoalDialogComponent, {
      minWidth: this.assessmentDialogWidth,
      maxWidth: this.careerGoalDialogWidth,
      width: this.careerGoalDialogWidth,
    });
  }

  redirectToBuildYourPlan() {
    if (this.checklistStore.generatePlanUnlocked()) {
      this.router.navigate(['/build-plan']);
    }
  }

  onClickItem(tag: ItemTags) {
    // Track checklist item selection
    switch (tag) {
      case 'resume':
        analytics.track('Checklist Resume', { section: this.section });
        this.openAddResumeDialog();
        break;
      case 'communication':
        analytics.track('Checklist Communication', { section: this.section });
        this.onClickStartAssessment(tag);
        break;
      case 'durable':
        analytics.track('Checklist Durable', { section: this.section });
        this.onClickStartAssessment(tag);
        break;
      case 'goal':
        analytics.track('Checklist Goal', { section: this.section });
        this.onClickSetGoal();
        break;
      case 'plan':
        analytics.track('Checklist Plan', { section: this.section });
        this.redirectToBuildYourPlan();
        break;
      case 'job':
        analytics.track('Checklist Job', { section: this.section });
        this.openJobLandingDialog();
        break;
      default:
        break;
    }
  }

  openJobLandingDialog() {
    const dialogRef = this.itemDialog.open(JobLandingDialogComponent, {
      panelClass: 'job-landing-dialog',
      width: '95%',
      maxWidth: '800px',
      maxHeight: '95vh',
      data: { entrypoint: 'checklist', section: this.section },
    });

    dialogRef.afterClosed().subscribe((result?: JobLandingDialogResult) => {
      if (result) {
        const sourceMap: Record<string, string> = {
          'Compass': 'compass',
          'Handshake': 'handshake',
          'LinkedIn': 'linked_in',
          'Networking or referral': 'networking_or_referral',
          'Other': 'other',
        };

        const payload: {
          work_type: string;
          employer_name: string;
          work_referrer?: string;
          work_referrer_other?: string;
        } = {
          work_type: result.roleType,
          employer_name: result.organization,
        };

        if (result.source) {
          const mappedSource = sourceMap[result.source];
          if (mappedSource && mappedSource !== 'other') {
            payload.work_referrer = mappedSource;
          } else {
            payload.work_referrer = 'other';
            payload.work_referrer_other = result.source;
          }
        }

        this.reportedWorkExperienceService.createReportedWorkExperience(payload).subscribe({
          next: () => this.markAsCompleted('job'),
          error: (err) => console.error('Failed to save reported work experience', err),
        });
      }
    });
  }
}
